'use client';

import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import type React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, db } from '@/lib/firebase/config';
import { getDocument } from '@/lib/firebase/firestore';
import type { Couple, User as UserModel } from '@/types/models';

interface AuthContextType {
  user: User | null;
  userProfile: UserModel | null;
  couple: Couple | null;
  coupleId: string | null;
  loading: boolean;
  loadState: undefined | 'loading' | 'syncing-couple' | 'ready';
  error: string | null;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  couple: null,
  coupleId: null,
  loading: true,
  loadState: undefined,
  error: null,
  refreshToken: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserModel | null>(null);
  const [couple, setCouple] = useState<Couple | null>(null);
  const [coupleId, setCoupleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<undefined | 'loading' | 'syncing-couple' | 'ready'>(undefined);
  const [error, setError] = useState<string | null>(null);
  const syncCompleteRef = useRef(false);

  const refreshToken = async () => {
    try {
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true); // Force refresh
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh token';
      console.error(message);
      throw err;
    }
  };

  // Subscribe to couple document in real-time
  useEffect(() => {
    // Update coupleId from userProfile
    const profileCoupleId = userProfile?.coupleId;
    setCoupleId(profileCoupleId || null);

    if (!profileCoupleId) {
      setCouple(null);
      return;
    }

    // Real-time subscription to couple document
    const unsubscribe = onSnapshot(
      doc(db, 'couples', profileCoupleId),
      (snapshot) => {
        if (snapshot.exists()) {
          setCouple({ id: snapshot.id, ...snapshot.data() } as Couple);
        } else {
          setCouple(null);
        }
      },
      (err) => {
        console.error('Error fetching couple:', err);
        setCouple(null);
      }
    );

    return () => unsubscribe();
  }, [userProfile?.coupleId]);

  // Sync coupleId from Firestore to custom claims
  useEffect(() => {
    async function syncCoupleId() {
      // Skip if already synced
      if (syncCompleteRef.current) return;

      // Only run when we have a user profile and auth is done loading
      if (!userProfile || loading) return;

      const currentUser = auth.currentUser;
      if (!currentUser) return;

      try {
        setLoadState('syncing-couple');

        // Get current token and check claims
        const idTokenResult = await currentUser.getIdTokenResult();
        const tokenCoupleId = idTokenResult.claims.coupleId as string | undefined;
        const firestoreCoupleId = userProfile.coupleId;

        // Check if claims match Firestore
        if (tokenCoupleId === firestoreCoupleId) {
          // Claims are in sync
          syncCompleteRef.current = true;
          setLoadState('ready');
          return;
        }

        console.log('Custom claims mismatch detected, refreshing token...');

        // Try refreshing the token first
        await refreshToken();

        // Check again after refresh
        const newIdTokenResult = await currentUser.getIdTokenResult();
        const newTokenCoupleId = newIdTokenResult.claims.coupleId as string | undefined;

        if (newTokenCoupleId === firestoreCoupleId) {
          console.log('Custom claims synced after token refresh');
          syncCompleteRef.current = true;
          setLoadState('ready');
          return;
        }

        // Still mismatched, call backend to sync claims
        console.log('Custom claims still mismatched, calling sync endpoint...');
        const token = await currentUser.getIdToken();

        const response = await fetch('/api/auth/sync-claims', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!data.success) {
          console.error('Failed to sync custom claims:', data.error);
          syncCompleteRef.current = true;
          setLoadState('ready');
          return;
        }

        // Refresh token one more time to get the updated claims
        await refreshToken();
        console.log('Custom claims synced successfully from backend');
        syncCompleteRef.current = true;
        setLoadState('ready');
      } catch (err) {
        console.error('Error syncing custom claims:', err);
        syncCompleteRef.current = true;
        setLoadState('ready');
      }
    }

    syncCoupleId();
  }, [userProfile, loading]);

  useEffect(() => {
    setLoadState('loading');

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        setUser(firebaseUser);
        setError(null);

        if (firebaseUser) {
          try {
            const profile = await getDocument<UserModel>('users', firebaseUser.uid);
            setUserProfile(profile);
          } catch (err) {
            console.error('Error fetching user profile:', err);
            setError('Failed to load user profile');
          }
        } else {
          setUserProfile(null);
          // If no user, we're ready immediately (no couple sync needed)
          setLoadState('ready');
        }

        setLoading(false);
        // Don't set ready here - let couple sync effect handle it when there's a user
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err.message);
        setLoading(false);
        setLoadState('ready');
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, userProfile, couple, coupleId, loading, loadState, error, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
